/**
 * Echo Notification Session Bridge
 *
 * Bridges Laravel Echo notification events into our shared notification store.
 * Mounted for authenticated users to receive notifications for the duration of their session.
 */

import { useEcho } from '@laravel/echo-react';
import { useCallback, useEffect, useRef } from 'react';

import { NotificationUtils } from '@personal-trainer-app/shared';

import type { EchoNotificationSessionBridgeProps } from './EchoNotificationSessionBridge.types';
import type React from 'react';

import { useNotificationStore } from '@/stores';

export const EchoNotificationSessionBridge: React.FC<
  EchoNotificationSessionBridgeProps
> = ({ username, debug = false, onNotification }) => {
  const addNotification = useNotificationStore(
    (state) => state.addNotification
  );
  const seenNotificationsRef = useRef<Set<string>>(new Set());

  const channelName = NotificationUtils.createUserNotificationChannel(username);

  const handleBroadcastEvent = useCallback(
    (payload: unknown) => {
      if (debug) {
        console.log(
          '[EchoNotificationSessionBridge] Raw broadcast event received:',
          {
            channelName,
            payload,
          }
        );
      }

      const notification =
        NotificationUtils.extractNotificationFromPayload(payload);

      if (!notification) {
        if (debug) {
          console.warn(
            '[EchoNotificationSessionBridge] Invalid notification payload:',
            {
              channelName,
              payload,
            }
          );
        }
        return;
      }

      // Prevent duplicate processing
      if (seenNotificationsRef.current.has(notification.id)) {
        if (debug) {
          console.log(
            '[EchoNotificationSessionBridge] Duplicate notification ignored:',
            {
              channelName,
              notificationId: notification.id,
            }
          );
        }
        return;
      }

      seenNotificationsRef.current.add(notification.id);

      if (debug) {
        console.log('[EchoNotificationSessionBridge] Notification processed:', {
          channelName,
          notificationId: notification.id,
          notificationType: notification.type,
        });
      }

      // Add to store and trigger callback
      addNotification(notification);
      onNotification?.(notification);
    },
    [addNotification, channelName, debug, onNotification]
  );

  // Listen for Laravel broadcast notification events
  useEcho<unknown>(
    channelName,
    '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
    handleBroadcastEvent,
    [handleBroadcastEvent],
    'private'
  );

  // Debug logging for channel subscription
  useEffect(() => {
    if (!debug) return;

    console.log(
      '[EchoNotificationSessionBridge] Subscribed to notification channel:',
      {
        username,
        channelName,
        expectedEchoChannelId: `private-${channelName}`,
      }
    );

    return () => {
      console.log(
        '[EchoNotificationSessionBridge] Unsubscribed from notification channel:',
        {
          username,
          channelName,
        }
      );
    };
  }, [channelName, debug, username]);

  // Cleanup seen notifications on unmount
  useEffect(() => {
    const seenNotifications = seenNotificationsRef.current;
    return () => {
      seenNotifications.clear();
    };
  }, []);

  return null;
};
